import { get } from 'lodash';
import { Spinner } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { HiCheck, HiChevronRight } from 'react-icons/hi';
import { useMutation, useQueryClient } from 'react-query';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { applyToDrivePositions } from 'src/apis/drive';
import Container from 'src/components/shared/Container';
import PositionDetailModal from 'src/components/placementDrives/PositionDetailModal';
import { MobileHeader, CompanyLogo, formatPositionTitle, MobileActionBar, PositionChips, PositionSubtitle, PrimaryButton, DRIVE_FONT_STYLE, useDriveFont, BackLink } from 'src/components/placementDrives/shared';
import { JobseekerDrivePosition } from 'types';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetDriveForJobseeker } from 'src/hooks/drives.hooks';

const PlacementDriveApplyPage = () => {
	useDriveFont();
	const { driveId } = useParams();
	const [searchParams] = useSearchParams();
	const preselected = searchParams.get('position');
	const navigate = useNavigate();
	const toast = useToast();
	const queryClient = useQueryClient();
	const { data, isLoading, refetch, isFetching } = useGetDriveForJobseeker(driveId);
	const { user } = useUserContext();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [detail, setDetail] = useState<JobseekerDrivePosition | null>(null);

	const applicable = (data?.positions ?? []).filter(position => !position.applicationStatus);

	useEffect(() => {
		if (!data) return;
		const available = data.positions.filter(position => !position.applicationStatus).map(position => position._id);
		setSelectedIds(preselected && available.includes(preselected) ? [preselected] : available);
	}, [data, preselected]);

	const applyFn = useMutation(applyToDrivePositions, {
		onSuccess() {
			queryClient.invalidateQueries(['getUpcomingDrivesForJobseeker']);
			queryClient.invalidateQueries(['getDriveForJobseeker', driveId]);
			// Drop the cached status so the status page loads the new applications instead of flashing the old ones.
			queryClient.removeQueries(['getMyDriveStatus', driveId]);
			navigate(`/placement-drives/${driveId}/status`, { replace: true });
		},
		onError(error: Error) {
			toast({ type: 'error', title: get(error, ['response', 'data', 'message'], error.message) });
		},
	});

	const toggle = (id: string) => setSelectedIds(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));

	if (user && (!user.profile?.resumeLink || user.profile.resumeLink === 'empty')) {
		return <Navigate to={`/placement-drives/${driveId}/resume`} replace />;
	}
	if (isLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spinner size="xl" />
			</div>
		);
	}
	if (!data?.drive) return <div className="py-24 text-center text-[#555770]">Drive not found.</div>;
	// Nothing to apply to (not pushed to any company yet): the drive page explains what to do.
	if (data.positions.length === 0) return <Navigate to={`/placement-drives/${driveId}`} replace />;
	if (!data.isApplicationOpen) return <div className="py-24 text-center text-[#555770]">Applications for this drive are closed.</div>;

	const selectedNames = applicable.filter(position => selectedIds.includes(position._id)).map(position => position.company.name);

	return (
		<Container className="dp-fade w-[92%] max-w-[1280px] pt-0 lg:pt-6 pb-32 lg:pb-24" style={DRIVE_FONT_STYLE}>
			<BackLink className="hidden lg:inline-flex" onClick={() => navigate(`/placement-drives/${driveId}`)}>{data.drive.name}</BackLink>
			<MobileHeader title="Apply to companies" onBack={() => navigate(`/placement-drives/${driveId}`)} />
			<div className="mt-5 lg:mt-6 grid gap-8 lg:grid-cols-[1fr_380px]">
				<div>
					<h1 className="text-[32px] leading-[36px] lg:text-[40px] lg:leading-[44px] tracking-[-0.03em] font-[800] text-[#111E2C] dark:text-white">Who do you want to meet?</h1>
					<p className="mt-2 text-[15px] lg:text-[16px] text-[#555770]">Pick as many companies as you like.</p>
					<div className="mt-6 rounded-[20px] border border-[#EEEEF5] bg-white dark:bg-[#1F2937] divide-y divide-[#EEEEF5] overflow-hidden">
						{data.positions.map(position => {
							const alreadyApplied = !!position.applicationStatus;
							const checked = selectedIds.includes(position._id);
							return (
							<React.Fragment key={position._id}>
								<button
									disabled={alreadyApplied}
									onClick={() => toggle(position._id)}
									className="hidden lg:flex w-full items-center gap-4 p-5 text-left disabled:opacity-60"
								>
									<span
										className={`w-7 h-7 rounded-full flex items-center justify-center border-2 ${checked || alreadyApplied ? 'bg-[#3027D8] border-[#3027D8] text-white' : 'border-[#C9CAD9]'}`}
									>
										{checked || alreadyApplied ? <HiCheck /> : null}
									</span>
									<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={52} />
									<div className="flex-1 min-w-0">
										<p className="text-[17px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</p>
										<p className="text-[14px] text-[#555770] dark:text-[#B0B8C4]">
											<PositionSubtitle position={position} />
										</p>
									</div>
									{alreadyApplied ? <span className="text-[13px] font-[600] text-[#8A8BA3]">Applied</span> : null}
								</button>
								<div className={`lg:hidden p-4 ${alreadyApplied ? 'opacity-70' : ''}`}>
									<div className="flex items-start gap-3">
										<button
											aria-label={`Select ${position.company.name}`}
											disabled={alreadyApplied}
											onClick={() => toggle(position._id)}
											className={`mt-2.5 w-7 h-7 shrink-0 rounded-full flex items-center justify-center border-2 ${checked || alreadyApplied ? 'bg-[#3027D8] border-[#3027D8] text-white' : 'border-[#C9CAD9]'}`}
										>
											{checked || alreadyApplied ? <HiCheck /> : null}
										</button>
										<button onClick={() => setDetail(position)} className="flex flex-1 min-w-0 items-center gap-3 text-left">
											<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={44} />
											<div className="flex-1 min-w-0">
												<p className="text-[16px] leading-[22px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</p>
												<p className="text-[14px] text-[#555770] dark:text-[#B0B8C4]">{formatPositionTitle(position.title)}</p>
											</div>
										</button>
										<button aria-label="View details" onClick={() => setDetail(position)} className="mt-2.5 p-1 text-[#8A8BA3]">
											<HiChevronRight className="h-5 w-5" />
										</button>
									</div>
									{/* Chips use the full row width (under the checkbox + logo) so they sit two or three per line. */}
									<div className="pl-10">
										<PositionChips position={position}>
											{alreadyApplied ? (
												<span className="inline-flex items-center gap-1 rounded-full bg-[#E6F6EC] px-2.5 py-1 text-[12.5px] leading-[18px] font-[600] text-[#1E7A44]">
													<HiCheck className="h-3.5 w-3.5" />
													Applied
												</span>
											) : null}
										</PositionChips>
									</div>
								</div>
							</React.Fragment>
							);
						})}
					</div>
				</div>
				<aside className="hidden lg:block h-fit rounded-[20px] border border-[#EEEEF5] bg-white dark:bg-[#1F2937] p-6 shadow-[0_2px_12px_rgba(20,20,60,0.06)]">
					<h3 className="text-[18px] font-[700] text-[#111E2C] dark:text-white pb-3 border-b border-[#EEEEF5]">Your selection</h3>
					<p className="py-4 text-[15px] font-[500] text-[#111E2C] dark:text-white border-b border-[#EEEEF5]">
						{selectedNames.length ? selectedNames.join(', ') : 'No company selected yet'}
					</p>
					<PrimaryButton
						className="w-full mt-4"
						disabled={!selectedIds.length || applyFn.isLoading}
						onClick={() => driveId && applyFn.mutate({ driveId, positionIds: selectedIds })}
					>
						{applyFn.isLoading ? 'Applying...' : `Apply to ${selectedIds.length} ${selectedIds.length === 1 ? 'company' : 'companies'}`}
					</PrimaryButton>
				</aside>
			</div>
			<MobileActionBar>
				<PrimaryButton
					className="w-full !py-4 !text-[17px]"
					disabled={!selectedIds.length || applyFn.isLoading}
					onClick={() => driveId && applyFn.mutate({ driveId, positionIds: selectedIds })}
				>
					{applyFn.isLoading ? 'Applying...' : `Apply to ${selectedIds.length || ''} ${selectedIds.length === 1 ? 'company' : 'companies'}`.replace('  ', ' ')}
				</PrimaryButton>
			</MobileActionBar>
			<PositionDetailModal
				position={detail}
				onClose={() => setDetail(null)}
				canApply
				actionSecondary={!!detail && selectedIds.includes(detail._id)}
				actionLabel={detail && selectedIds.includes(detail._id) ? 'Remove from selection' : 'Add to my selection'}
				onApply={position => {
					toggle(position._id);
					setDetail(null);
				}}
			/>
		</Container>
	);
};

export default PlacementDriveApplyPage;
